import React, { useCallback, useContext, useMemo, useState } from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Typography,
  Divider,
} from '@material-ui/core';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { AxiosResponse } from 'axios';
import { Controller, useForm } from 'react-hook-form';
import DateFnsUtils from '@date-io/date-fns';
import dayjs from 'dayjs';

import {
  Image,
  PersonalForm,
  UpdateIAMUserData,
  UpdateUser,
  UploadAvatarData,
  User,
  VerifyPhoneData,
} from '@vyce/core/src/types';
import { AppKeyboardDatePicker } from '@vyce/core/src/components/inputs';
import {
  CSCSRegistrationNumberField,
  FirstNameField,
  GenderField,
  LastNameField,
  NationalityField,
  NiNumberField,
} from '@vyce/core/src/components/controlled-inputs';
import { AppFormWrapper, ChangePasswordField, ReCAPTCHAComponent } from '@vyce/core/src/components';
import { DeviceContext } from '@vyce/core/src/contexts';
import { formatDate } from '@vyce/core/src/utils/dates';
import { checkNINumber } from '@vyce/core/src/utils/onboarding';
import { ChangePasswordData } from '@vyce/core/src/api/types';
import { capitalizeFirstLetter } from '@vyce/core/src/utils';
import { useRecaptcha } from '@vyce/core/src/hooks';
import { ensureUserExistsRequest } from '@vyce/core/src/api/chat';
import { userToChatUser } from '@vyce/core/src/utils/chat';

import { ProfileAvatarUploader } from '../components/ProfileAvatarUploader';
import { EditEmailOrVerify } from '../components/EditEmailOrVerify';
import { EditPhoneOrVerify } from '../components/EditPhoneOrVerify';
import useStyles from '../styles';
import { MAIN_CONTAINER_ID } from '../../../constants';
import { NotificationContext } from '../../../contexts/notificationContext';
import { EditAddress } from '../components/EditAddress';

const maxDate = new Date().setFullYear(new Date().getFullYear() - 16);
const minDate = new Date(1900, 0, 1);
const INVALID_DATE_OF_BIRTH_MESSAGE = 'Please add your actual date of birth';
const emptyRules = {};

const dateOfBirthRules = {
  validate: (value?: string | Date | null) => {
    if (!value) {
      return true;
    }
    const date = dayjs(value);
    const isValid = date.isValid() && !date.isAfter(dayjs(maxDate)) && !date.isBefore(dayjs(minDate));

    return isValid || INVALID_DATE_OF_BIRTH_MESSAGE;
  },
};

interface Props {
  uploadUserAvatar: ({ file, saveToProfile, handleServerError }: UploadAvatarData) => void;
  user: User;
  token: string;
  userDataLoading?: boolean;
  isLegend?: boolean;
  updateUser: (data: UpdateUser) => void;
  verifyPhone?: (data: VerifyPhoneData) => void;
  changePasswordRequest: (data: ChangePasswordData, userId?: string) => Promise<AxiosResponse>;
}

export const UserPersonalInfo: React.FC<Props> = ({
  uploadUserAvatar,
  user,
  token,
  userDataLoading = false,
  isLegend,
  updateUser,
  verifyPhone,
  changePasswordRequest,
}) => {
  const classes = useStyles();
  const { showNotification, handleServerError } = useContext(NotificationContext);
  const [openWarningDialog, setOpenWarningDialog] = useState<boolean>(false);
  const [sensitiveData, setSensitiveData] = useState<string[]>([]);
  const [showAvatarButtons, setShowAvatarButtons] = useState<boolean>(false);
  const { reCaptchaRef, getReCaptchaToken } = useRecaptcha();

  const phoneVerified = useMemo(() => {
    return !!user?.phone_verified;
  }, [user]);
  const methods = useForm<PersonalForm & { avatar?: Image; gender?: { value: string; name: string } }>({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: {
      first_name: user.first_name,
      last_name: user.last_name,
      phone: user.phone || undefined,
      email: user.email || undefined,
      gender: user.gender ? { value: user.gender, name: capitalizeFirstLetter(user.gender) } : undefined,
      date_of_birth: user.date_of_birth || undefined,
      address: user.address || {
        address_snippet: undefined,
        address_line_4: undefined,
        address_line_2: undefined,
        address_line_1: undefined,
        address_line_3: undefined,
      },
      nationality: user.nationality || undefined,
      ni_number: user.context?.ni_number || undefined,
      cscs_ref_number: user.context?.cscs_ref_number || undefined,
      share_code: user.context?.share_code || undefined,
      avatar: { file: null, url: user.photo?.url },
    },
  });
  const {
    control,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = methods;
  const avatar = watch('avatar');
  const { isMobile } = useContext(DeviceContext);

  // Email/phone are changed through dedicated flows that don't refresh the user model,
  // so push the new value straight into Firebase here. The onUserProfileUpdate trigger
  // then fans it out to the denormalised profiles in every chat.
  const syncContactToFirebase = useCallback(
    (changes: { email?: string; phone?: string }) => {
      if (!user.uuid) {
        return;
      }
      ensureUserExistsRequest({ ...userToChatUser(user), ...changes }).catch(e => console.error(e));
    },
    [user]
  );

  const setAvatar = useCallback(
    (newAvatar: Image) => {
      setValue('avatar', newAvatar);
    },
    [setValue]
  );

  const handleSubmit = async ({ avatar, ...data }: PersonalForm & { avatar?: Image }) => {
    const sensitiveItems = [];
    if (data.ni_number && data.ni_number !== user.context?.ni_number) {
      sensitiveItems.push('NI Number');
    }
    if (data.last_name !== user.last_name) {
      sensitiveItems.push('Last Name');
    }
    if (data.date_of_birth !== undefined && data.date_of_birth !== user.date_of_birth) {
      sensitiveItems.push('Date of Birth');
    }
    setSensitiveData(sensitiveItems);

    if (avatar?.file) {
      await uploadUserAvatar({ file: avatar.file, saveToProfile: true, handleServerError });
      setShowAvatarButtons(false);
    }

    if (sensitiveItems?.length) {
      setOpenWarningDialog(true);
    } else {
      update(data);
    }
  };

  const update = async (data: PersonalForm) => {
    setOpenWarningDialog(false);
    const payloadData = await prepareUserData(data);
    if (data.ni_number && data.ni_number !== user.context?.ni_number) {
      const recaptcha_token = await getReCaptchaToken();
      if (!recaptcha_token) {
        return;
      }
      const isNINumberAvailable = await checkNINumber(data.ni_number, recaptcha_token, showNotification);
      if (!isNINumberAvailable) {
        return;
      }
    }
    updateUser({ data: payloadData, showNotification, handleServerError });
  };

  const prepareUserData = async (data: PersonalForm): Promise<UpdateIAMUserData> => {
    const userData: UpdateIAMUserData = {
      first_name: data.first_name,
      last_name: data.last_name,
      gender: data.gender?.value || null,
      date_of_birth: data.date_of_birth ? formatDate(data.date_of_birth) : null,
      ni_number: data.ni_number,
      nationality_id: data.nationality?.uuid || null,
      cscs_ref_number: data.cscs_ref_number || null,
    };

    return userData;
  };

  return (
    <Box paddingBottom={10}>
      <AppFormWrapper
        methods={methods}
        initialData={user}
        prePopulateValues={false}
        handleSubmit={handleSubmit}
        loading={userDataLoading}
      >
        <Grid container spacing={3} className={classes.infoContainer}>
          <Grid item xs={12} lg={3}>
            <ProfileAvatarUploader
              avatar={avatar}
              oldAvatarUrl={user.photo?.url}
              setAvatar={setAvatar}
              loading={userDataLoading}
              showAvatarButtons={showAvatarButtons}
              setShowAvatarButtons={setShowAvatarButtons}
            />
          </Grid>

          <Grid item xs={12} lg={9}>
            <Paper className={classes.formPaper} variant="outlined">
              <Box marginBottom={2} display="flex">
                <Typography variant="h6" className={classes.name} align="center">
                  General Info
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <EditEmailOrVerify
                    isLegend={isLegend}
                    email={user.email}
                    userId={user.uuid}
                    verified={!!user.email_verified}
                    getReCaptchaToken={getReCaptchaToken}
                    onSaved={email => syncContactToFirebase({ email })}
                  />
                </Grid>

                <Grid item xs={12}>
                  <EditPhoneOrVerify
                    isLegend={isLegend}
                    phone={user.phone}
                    userId={user.uuid}
                    verified={phoneVerified}
                    verifyPhone={verifyPhone}
                    getReCaptchaToken={getReCaptchaToken}
                    onSaved={phone => syncContactToFirebase({ phone })}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={12}>
                  <EditAddress isLegend={isLegend} address={user.address} userId={user.uuid} />
                </Grid>

                <Grid item xs={12} lg={6}>
                  <FirstNameField />
                </Grid>

                <Grid item xs={12} lg={6}>
                  <LastNameField />
                </Grid>

                <Grid item xs={12} lg={6}>
                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <Controller
                      name="date_of_birth"
                      control={control}
                      rules={dateOfBirthRules}
                      render={props => (
                        <AppKeyboardDatePicker
                          value={props.field.value || null}
                          onChange={data => props.field.onChange(formatDate(data))}
                          label="Date of Birth"
                          format="dd/MM/yyyy"
                          error={!!errors.date_of_birth}
                          helperText={(errors.date_of_birth?.message as string) || ''}
                          fullWidth
                          disableFuture
                          minDate={minDate}
                          maxDate={maxDate}
                          minDateMessage={INVALID_DATE_OF_BIRTH_MESSAGE}
                          maxDateMessage={INVALID_DATE_OF_BIRTH_MESSAGE}
                          invalidDateMessage={INVALID_DATE_OF_BIRTH_MESSAGE}
                        />
                      )}
                    />
                  </MuiPickersUtilsProvider>
                </Grid>

                <Grid item xs={12} lg={6}>
                  <NationalityField rules={emptyRules} />
                </Grid>

                <Grid item xs={12} lg={6}>
                  <NiNumberField />
                </Grid>

                <Grid item xs={12} lg={6}>
                  <CSCSRegistrationNumberField name="cscs_ref_number" />
                </Grid>

                <Grid item xs={12} lg={6}>
                  <GenderField />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </AppFormWrapper>

      <Grid container spacing={2} style={{ marginTop: 16 }}>
        <Grid item xs={12} lg={9}>
          {user.email && (
            <Paper className={classes.formPaper} variant="outlined">
              <Typography variant="h6">Account Details</Typography>

              <Box marginTop={2}>
                <ChangePasswordField
                  user={user}
                  isLegend={isLegend}
                  changePasswordRequest={changePasswordRequest}
                  token={token}
                  email={user.email}
                />
              </Box>
            </Paper>
          )}
        </Grid>
      </Grid>

      <Dialog
        container={document.getElementById(MAIN_CONTAINER_ID)}
        fullScreen={isMobile}
        open={openWarningDialog}
        onClose={() => setOpenWarningDialog(false)}
      >
        <DialogTitle>You may lose data. Are you sure?</DialogTitle>
        <DialogContent>
          If you change {sensitiveData?.join(', ')}, we will recheck all you qualifications.
        </DialogContent>
        <DialogActions>
          <Button size="large" variant="outlined" onClick={() => setOpenWarningDialog(false)}>
            Cancel
          </Button>
          <Button size="large" onClick={() => update(getValues())} color="primary" variant="contained">
            Ok, Update
          </Button>
        </DialogActions>
      </Dialog>
      <ReCAPTCHAComponent ref={reCaptchaRef} />
    </Box>
  );
};
